import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { Client } from '../../services/api'

export interface ClientPickerProps {
  options: Client[]
  value: Client | null
  onChange: (client: Client | null) => void
  isManager: boolean
  assignedClientIds: Set<string>
  /**
   * Whether to group options into "My Clients" / "All Clients". Defaults to
   * `isManager`, which is who used to be the only one offered other people's
   * clients. Job Applications offers them to every role and passes true.
   */
  groupOptions?: boolean
  /** Overrides the default `{ minWidth: 260 }` sizing. */
  sx?: SxProps<Theme>
  label?: string | null
  placeholder?: string
  /** Shows a spinner while `options` is still being read. */
  loading?: boolean
  /**
   * The detail that tells two same-named clients apart, shown under the name in
   * the list and after it in the field. It is the address or number the message
   * will actually go to on the compose pickers, so it is not decoration there.
   */
  secondary?: (client: Client) => string | undefined
  /**
   * `standard` renders the borderless field the email compose header is built
   * around, where every row is an underline-less label and value.
   */
  inputVariant?: 'outlined' | 'standard'
  /** Says why the list is empty when "No options" would not. */
  noOptionsText?: string
  /**
   * Adds a first option with this label, offered while a client is selected,
   * that clears the selection. The clear button alone is not enough on a page
   * where clearing is a destination: MUI only shows it on hover or focus.
   * When set, the unassigned group is headed "Other Clients" rather than "All
   * Clients", because that header is not clickable and reads as this option.
   */
  clearOptionLabel?: string
}

const CLEAR_OPTION_ID = '__clear__'

interface ClearOption {
  id: typeof CLEAR_OPTION_ID
  name: string
}

type PickerOption = Client | ClearOption

const isClearOption = (o: PickerOption): o is ClearOption => o.id === CLEAR_OPTION_ID

/**
 * Shared client switcher — extracted from JobApplicationsTracker (MWA task 14)
 * so the upcoming Networking page can reuse it. Behavior is unchanged from the
 * inline original: for managers, options are grouped into "My Clients"
 * (assignedClientIds) ahead of "All Clients" via MUI's groupBy (which groups
 * consecutive same-group options — callers are expected to have already
 * sorted `options` mine-first); non-managers see a flat list.
 */
export default function ClientPicker({
  options,
  value,
  onChange,
  isManager,
  assignedClientIds,
  groupOptions,
  sx,
  label = 'Client',
  placeholder = 'Search clients',
  loading,
  secondary,
  inputVariant = 'outlined',
  noOptionsText,
  clearOptionLabel,
}: ClientPickerProps) {
  const grouped = groupOptions ?? isManager
  const standard = inputVariant === 'standard'
  const otherGroup = clearOptionLabel ? 'Other Clients' : 'All Clients'
  const allOptions: PickerOption[] =
    clearOptionLabel && value ? [{ id: CLEAR_OPTION_ID, name: clearOptionLabel }, ...options] : options

  return (
    <Autocomplete<PickerOption>
      size="small"
      sx={sx ?? { minWidth: 260 }}
      options={allOptions}
      loading={loading}
      noOptionsText={noOptionsText}
      groupBy={
        grouped
          ? (c) => (isClearOption(c) ? '' : assignedClientIds.has(c.id) ? 'My Clients' : otherGroup)
          : undefined
      }
      // The clear option sits in a group of its own so it is not filed under a
      // client group; that group's header is empty and hidden here.
      slotProps={
        clearOptionLabel
          ? { listbox: { sx: { '& .MuiAutocomplete-groupLabel:empty': { display: 'none' } } } }
          : undefined
      }
      getOptionLabel={(c) => {
        if (isClearOption(c)) return c.name
        const name = c.name || c.email || '(unnamed client)'
        const detail = secondary?.(c)
        return detail ? `${name} (${detail})` : name
      }}
      value={value}
      onChange={(_, v) => onChange(v && !isClearOption(v) ? v : null)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      renderOption={
        secondary
          ? (props, c) =>
              isClearOption(c) ? (
                <li {...props} key={c.id}>
                  {c.name}
                </li>
              ) : (
                <li {...props} key={c.id}>
                  <Box>
                    <Typography variant="body2">{c.name || '(unnamed client)'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {secondary(c)}
                    </Typography>
                  </Box>
                </li>
              )
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant={inputVariant}
          label={label || undefined}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            // The standard field is only used inside a header that draws its own
            // separators, where MUI's underline would double them up.
            ...(standard ? { disableUnderline: true } : {}),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
